import axios from "axios";

export const api = axios.create({
  baseURL: process.env.REACT_APP_ECOM_API_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sy_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("sy_token");
      window.dispatchEvent(new Event("sy:unauthorized"));
    }
    return Promise.reject(err);
  }
);

export const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n ?? 0);

export const errMsg = (e) => {
  const d = e?.response?.data;
  if (d && typeof d === "object" && d.detail) return d.detail;
  const s = e?.response?.status;
  if (s === 429) return "Too many attempts — please wait a moment and retry";
  if (s === 502 || s === 503 || s === 504) return "The service hiccuped — please wait a few seconds and try again";
  if (s === 404) return "Not found — this may have moved";
  if (s) return `Something went wrong (error ${s}) — please try again`;
  return "Network issue — check your connection and retry";
};

export const totalStock = (p) =>
  p?.total_stock ?? Object.values(p?.current_stock || {}).reduce((s, q) => s + (Number(q) || 0), 0);

export const FALLBACK_IMG = "https://images.unsplash.com/photo-1612817288484-6f916006741a?q=80&w=800&auto=format&fit=crop";

export const productImage = (p) =>
  p?.images?.[0] || p?.primary_image || p?.thumbnail || FALLBACK_IMG;

// Server-driven pricing (ERP computes everything — never recalculate here):
// price = offer_price (real sale price) → fallback selling_price
// listPrice = selling_price, struck through only when offer_price < selling_price
// mrp = shown as MRP label; off = discount_percent (% off MRP) for the badge
export const resolvePricing = (p) => {
  const price = p?.offer_price ?? p?.selling_price ?? 0;
  const listPrice = p?.selling_price ?? 0;
  const mrp = p?.mrp ?? 0;
  const off = Math.round(p?.discount_percent || 0);
  const offer = p?.active_offer || null;
  return { price, listPrice, mrp, off, offer };
};

export const offerLabel = (offer) => {
  if (!offer) return "";
  if (offer.offer_type === "percentage_discount")
    return `${offer.code ? `${offer.code} ` : ""}${offer.discount_value}% OFF`;
  if (offer.buy_qty > 0 && offer.get_qty > 0) return `BUY ${offer.buy_qty} GET ${offer.get_qty}`;
  return offer.name || "OFFER";
};

// Cart/order lines carry a bare offer_name like "20" — append % when numeric
export const offerNameLabel = (name) => {
  if (!name) return "";
  return /^[\d.]+$/.test(String(name).trim()) ? `${name}%` : name;
};

// Logistics display helpers — never show 0 g / 0 kg to customers
export const shippingInfo = (p) => {
  const s = p?.shipping || {};
  const estimated = s.weight_is_estimated === true;
  const grams = s.weight_grams || 0;
  const kg = s.weight_kg || 0;
  const dims = [s.length_cm, s.width_cm, s.height_cm];
  const hasDims = dims.every((d) => d > 0);
  const chargeable = s.chargeable_weight_kg || 0;
  return { estimated, grams, kg, dims, hasDims, chargeable };
};

// Active offer percent from the offers module (percentage_discount offers only)
export const offerPct = (p) =>
  p?.active_offer?.offer_type === "percentage_discount" ? Number(p.active_offer.discount_value) || 0 : 0;

// Homepage rails: only products with a real image (never the fallback placeholder)
export const hasRealImage = (p) => !!(p?.images?.length || p?.primary_image || p?.thumbnail);

// Round-robin one-per-brand first so rails show different brands, then fill by sort order
export const pickBrandDiverse = (products, limit = 10) => {
  const first = [];
  const rest = [];
  const seen = new Set();
  for (const p of products) {
    const b = p.brand_id || p.id;
    if (!seen.has(b)) { seen.add(b); first.push(p); } else rest.push(p);
  }
  return [...first, ...rest].slice(0, limit);
};

// Fetch the full catalog — needed because the ERP list API can't filter by
// offer price/discount server-side. All pages fire in PARALLEL (no waterfall),
// onProgress delivers partial pools as pages land (fast first paint), and
// results are cached in sessionStorage for 5 min + in-memory per session.
const CATALOG_TTL = 5 * 60 * 1000;
const CATALOG_MAX_PAGES = 10;
const catalogCache = new Map();

const slimProduct = (p) => ({
  id: p.id, slug: p.slug, name: p.name, brand_id: p.brand_id, category_id: p.category_id,
  images: p.images, primary_image: p.primary_image, thumbnail: p.thumbnail,
  offer_price: p.offer_price, selling_price: p.selling_price, mrp: p.mrp,
  discount_percent: p.discount_percent, active_offer: p.active_offer,
  in_stock: p.in_stock, total_stock: p.total_stock, branch_stocks: p.branch_stocks,
});

const loadCatalogPages = (extraParams, onProgress, cacheKey) => {
  const base = { page_size: 100, ...extraParams };
  const byPage = new Map();
  const emit = () => {
    const items = [...byPage.keys()].sort((a, b) => a - b).flatMap((pg) => byPage.get(pg));
    onProgress?.(items);
  };
  const fetchPage = (pg) =>
    api.get("/ecom/catalog/products", { params: { ...base, page: pg } })
      .then((r) => ({ pg, data: r.data || {} }))
      .catch(() => ({ pg, data: null }));

  const jobs = Array.from({ length: CATALOG_MAX_PAGES }, (_, i) =>
    fetchPage(i + 1).then(({ pg, data }) => {
      const totalPages = data?.total_pages ?? 1;
      if (pg > totalPages) return { totalPages };
      const items = data?.items || [];
      if (items.length) { byPage.set(pg, items); emit(); }
      return { totalPages };
    })
  );

  return Promise.all(jobs).then(async (results) => {
    const totalPages = Math.max(...results.map((r) => r?.totalPages || 1));
    // safety: catalog grew beyond the optimistic page window
    for (let pg = CATALOG_MAX_PAGES + 1; pg <= totalPages; pg++) {
      const { data } = await fetchPage(pg);
      const items = data?.items || [];
      if (items.length) { byPage.set(pg, items); emit(); }
    }
    const items = [...byPage.keys()].sort((a, b) => a - b).flatMap((pg) => byPage.get(pg));
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), items: items.map(slimProduct) })); } catch { /* quota */ }
    return items;
  });
};

export const fetchAllProducts = (extraParams = {}, onProgress) => {
  const key = JSON.stringify(extraParams);
  const cacheKey = `sy_catalog_${key}`;

  // warm session cache → instant render, refresh in background
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
    if (cached && Date.now() - cached.at < CATALOG_TTL && cached.items?.length) {
      onProgress?.(cached.items);
      const bg = loadCatalogPages(extraParams, onProgress, cacheKey);
      catalogCache.set(key, bg);
      return Promise.resolve(cached.items);
    }
  } catch { /* no cache */ }

  if (catalogCache.has(key)) return catalogCache.get(key);
  const promise = loadCatalogPages(extraParams, onProgress, cacheKey);
  catalogCache.set(key, promise);
  return promise;
};

export const cardProduct = (p, brandName) => {
  const { price, listPrice, mrp, off, offer } = resolvePricing(p);
  return {
    id: p.id,
    slug: p.slug || p.id,
    name: p.name,
    brand: brandName || "",
    img: productImage(p),
    price,
    listPrice,
    mrp,
    off,
    offer,
    stock: totalStock(p),
    inStock: p.in_stock ?? totalStock(p) > 0,
  };
};
